import { parseReadInstruction } from './readAnalyzer.js';

const BTN_PRSD_PATTERN = /\[@BTN_PRSD\]\s*(=|==)\s*("[^"]+"|'[^']+'|\[@?[A-Z0-9_]+\]|[A-Z0-9_]+)/i;
const DIRECT_BUTTON_PATTERN = /\[(@(?:OK|EXIT|BTN_L25_[0-9]+|BTN_[A-Z0-9_]+))\]/i;
const THEN_PATTERN = /\bTHEN\b\s*(.*)$/i;
const COMMAND_PATTERN = /^(GOTO|CALL|WINDOW|EXIT|WRITE|READ)\b\s*(.*)$/i;
const CONDITION_PATTERN = /^\s*\[(@?[A-Z0-9_#]+)\]\s*(<>|!=|=|==|<=|>=|<|>)\s*(".*?"|'.*?'|\[@?[A-Z0-9_#]+\]|TRUE|FALSE|[^\s]+)\s*$/i;
const SUFFIX_PATTERN = /^(.*?)(#[A-Z])$/i;

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

function cleanButtonName(value) {
  return value.trim().replace(/^\[/, '').replace(/\]$/, '').replace(/^@/, '').replace(/^["']|["']$/g, '');
}

function getNodeRaw(node) {
  return node.normalizedRaw || node.raw || node.expression || node.text || '';
}

function parseTechnicalSuffix(variableName = '') {
  const match = variableName.match(SUFFIX_PATTERN);

  return match
    ? {
        variable: match[1],
        suffix: match[2].toUpperCase(),
      }
    : {
        variable: variableName,
        suffix: null,
      };
}

function normalizeVariableName(variableName = '') {
  return parseTechnicalSuffix(variableName).variable;
}

function normalizeOperator(operator) {
  if (operator === '==') {
    return '=';
  }

  if (operator === '!=') {
    return '<>';
  }

  return operator;
}

function normalizeValue(rawValue = '') {
  const value = rawValue.trim();

  if (/^\[(@?[A-Z0-9_#]+)\]$/i.test(value)) {
    const variable = value.slice(1, -1);

    return {
      kind: 'variable',
      variable: normalizeVariableName(variable),
      suffix: parseTechnicalSuffix(variable).suffix,
    };
  }

  const unquotedValue =
    (value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))
      ? value.slice(1, -1)
      : value;

  if (/^TRUE$/i.test(unquotedValue)) {
    return true;
  }

  if (/^FALSE$/i.test(unquotedValue)) {
    return false;
  }

  return unquotedValue;
}

function cleanInlineActionText(text = '') {
  return text.replace(/\b(?:ENDIF|ELSE|THEN)\b.*$/i, '').trim();
}

function extractActionTarget(expression = '') {
  const cleanExpression = cleanInlineActionText(expression);
  const targetMatch = cleanExpression.match(/^([A-Z0-9_.-]+)/i);

  return targetMatch ? targetMatch[1] : '';
}

function extractSabreCommandBody(text = '') {
  const normalizedText = text.trim().replace(/\u00c2(?=[\u00a4\u00af\u00ae\ufffd])/g, '');

  if (!/^[\u00a4\u00af\ufffd]/.test(normalizedText)) {
    return null;
  }

  return normalizedText.slice(1).replace(/[\u00ae\u00a4\ufffd]+$/g, '');
}

function parseCommandTemplate(text = '') {
  const commandBody = extractSabreCommandBody(text);

  if (!commandBody) {
    return null;
  }

  const template = commandBody
    .replace(/\{ENTER\}/gi, '')
    .replace(/[\u009d\u00a4\ufffd]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\[(@?[A-Z0-9_#]+)\]/gi, (_match, variableName) => `{${normalizeVariableName(variableName)}}`)
    .trim();

  return {
    type: 'command',
    template,
  };
}

function parseCondition(conditionText) {
  const match = conditionText.match(CONDITION_PATTERN);

  if (!match) {
    return null;
  }

  const left = parseTechnicalSuffix(match[1]);
  const right = normalizeValue(match[3]);
  const baseCondition = {
    left: left.variable,
    operator: normalizeOperator(match[2]),
  };

  if (right && typeof right === 'object' && right.kind === 'variable') {
    return {
      ...baseCondition,
      leftSuffix: left.suffix,
      right: right.variable,
      rightType: 'variable',
      rightSuffix: right.suffix,
    };
  }

  return {
    ...baseCondition,
    suffix: left.suffix,
    right,
    rightType: 'literal',
  };
}

function parseConditions(expression = '') {
  const conditionSource = expression.replace(/\bTHEN\b.*$/i, '').trim();
  const tokens = conditionSource.split(/\s+(AND|OR)\s+/i);
  const conditions = [];

  for (let index = 0; index < tokens.length; index += 2) {
    if (index > 0) {
      conditions.push({
        join: tokens[index - 1].toUpperCase(),
      });
    }

    const condition = parseCondition(tokens[index]);

    if (condition) {
      conditions.push(condition);
    }
  }

  return conditions;
}

function parseIfNode(node, extras = {}) {
  const inlineAction = findInlineAction(node.expression || '');
  const parsedIf = {
    type: 'if',
    conditions: parseConditions(node.expression || ''),
    line: node.line,
    raw: node.raw,
  };

  if (inlineAction) {
    parsedIf.action = inlineAction;
  }

  return {
    ...parsedIf,
    ...extras,
  };
}

function parseRequireNode(node) {
  const condition = parseCondition(node.expression || '');

  if (!condition) {
    return {
      type: 'require',
      expression: node.expression,
      line: node.line,
      raw: node.raw,
      parsed: false,
    };
  }

  return {
    type: 'require',
    variable: condition.left,
    suffix: condition.suffix || condition.leftSuffix,
    operator: condition.operator,
    value: condition.rightType === 'literal' ? condition.right : undefined,
    rightVariable: condition.rightType === 'variable' ? condition.right : undefined,
    rightSuffix: condition.rightSuffix,
    line: node.line,
    raw: node.raw,
  };
}

function parseReadNode(node) {
  return {
    ...parseReadInstruction(node.normalizedRaw || node.raw || node.expression || ''),
    line: node.line,
    raw: node.raw,
    normalizedRaw: node.normalizedRaw,
  };
}

function parseComputeNode(node) {
  const expression = node.expression || '';
  const match = expression.match(/^\[([A-Z0-9_#]+)\]\s*=\s*(.+)$/i);

  return {
    type: 'compute',
    target: match ? normalizeVariableName(match[1]) : null,
    expression: match
      ? match[2].replace(/\[([A-Z0-9_#]+)\]/gi, (_token, variableName) => normalizeVariableName(variableName)).replace(/\s+/g, '')
      : expression,
    line: node.line,
    raw: node.raw,
  };
}

function normalizeLogicNode(node) {
  if (node.type === 'sabre_special_sequence') {
    return {
      type: 'sabre_special_sequence',
      sequence: node.sequence,
      sequences: node.sequences || [],
      visible: false,
      parseStatus: node.parseStatus || 'parsed',
      line: node.line,
      raw: node.raw,
      normalizedRaw: node.normalizedRaw,
    };
  }

  if (node.type === 'unknown' || node.type === 'command') {
    const commandAction = parseCommandTemplate(getNodeRaw(node));

    if (commandAction) {
      return {
        ...commandAction,
        line: node.line,
        raw: node.raw,
        normalizedRaw: node.normalizedRaw,
      };
    }
  }

  if (node.type === 'require') {
    return parseRequireNode(node);
  }

  if (node.type === 'read') {
    return parseReadNode(node);
  }

  if (node.type === 'compute') {
    return parseComputeNode(node);
  }

  return {
    type: node.type,
    expression: node.expression,
    line: node.line,
    raw: node.raw,
  };
}

function getThenAction(node) {
  const raw = getNodeRaw(node).trim();

  if (/^THEN\b/i.test(raw)) {
    return parseActionText(raw.replace(/^THEN\b/i, '').trim());
  }

  return nodeToAction(node);
}

function getElseAction(node) {
  if (node.type === 'else') {
    return parseActionText(node.expression || '');
  }

  return nodeToAction(node);
}

function findBranchAction(astNodes, startIndex, stopTypes, actionGetter, requiresThenMarker = true) {
  let hasThenMarker = !requiresThenMarker;

  for (let index = startIndex; index < astNodes.length; index += 1) {
    const node = astNodes[index];
    const raw = getNodeRaw(node).trim();

    if (stopTypes.includes(node.type)) {
      return null;
    }

    if (/^THEN\b/i.test(raw)) {
      hasThenMarker = true;
      const action = actionGetter(node);

      if (action) {
        return {
          action,
          index,
          sourceNode: node,
        };
      }

      continue;
    }

    if (!hasThenMarker) {
      continue;
    }

    const action = actionGetter(node);

    if (action) {
      return {
        action,
        index,
        sourceNode: node,
      };
    }
  }

  return null;
}

function findFalseAction(astNodes, elseIndex) {
  if (elseIndex === -1) {
    return null;
  }

  const elseNode = astNodes[elseIndex];
  const inlineElseAction = getElseAction(elseNode);

  if (inlineElseAction) {
    return {
      action: inlineElseAction,
      index: elseIndex,
      sourceNode: elseNode,
    };
  }

  return findBranchAction(astNodes, elseIndex + 1, ['endif'], getElseAction, false);
}

function findElseIndex(astNodes, ifIndex) {
  for (let index = ifIndex + 1; index < astNodes.length; index += 1) {
    if (astNodes[index].type === 'endif') {
      return -1;
    }

    if (astNodes[index].type === 'else') {
      return index;
    }
  }

  return -1;
}

function buildLogic(astNodes, skipLines = new Set()) {
  const consumed = new Set();
  const logic = [];

  astNodes.forEach((node, index) => {
    if (skipLines.has(node.line)) {
      return;
    }

    if (consumed.has(index)) {
      return;
    }

    if (node.type === 'if') {
      const inlineAction = findInlineAction(node.expression || '');

      if (inlineAction) {
        logic.push(parseIfNode(node));
        return;
      }

      const elseIndex = findElseIndex(astNodes, index);
      const trueAction = findBranchAction(astNodes, index + 1, ['else', 'endif'], getThenAction);
      const falseAction = findFalseAction(astNodes, elseIndex);
      const extras = {};

      if (trueAction?.action) {
        extras.trueAction = trueAction.action;
        consumed.add(trueAction.index);
      }

      if (falseAction?.action) {
        extras.falseAction = falseAction.action;
        consumed.add(falseAction.index);
      }

      logic.push(parseIfNode(node, extras));
      return;
    }

    if (['else', 'endif'].includes(node.type)) {
      return;
    }

    if (['goto', 'write', 'read', 'compute', 'require', 'exit', 'command', 'sabre_special_sequence'].includes(node.type)) {
      logic.push(normalizeLogicNode(node));
      return;
    }

    if (node.type === 'unknown' && parseCommandTemplate(getNodeRaw(node))) {
      logic.push(normalizeLogicNode(node));
    }
  });

  return logic;
}

function parseButtonTrigger(expression) {
  const btnPrsdMatch = expression.match(BTN_PRSD_PATTERN);

  if (btnPrsdMatch) {
    return {
      type: 'button',
      source: 'BTN_PRSD',
      button: cleanButtonName(btnPrsdMatch[2]),
    };
  }

  const directButtonMatch = expression.match(DIRECT_BUTTON_PATTERN);

  if (directButtonMatch) {
    return {
      type: 'button',
      source: cleanButtonName(directButtonMatch[1]),
      button: cleanButtonName(directButtonMatch[1]),
    };
  }

  return null;
}

function parseActionText(text) {
  const commandAction = parseCommandTemplate(text);

  if (commandAction) {
    return commandAction;
  }

  const commandMatch = text.trim().match(COMMAND_PATTERN);

  if (!commandMatch) {
    return null;
  }

  const command = commandMatch[1].toLowerCase();
  const expression = commandMatch[2].trim();

  if (command === 'goto') {
    return {
      type: 'goto',
      target: extractActionTarget(expression),
    };
  }

  if (command === 'call') {
    return {
      type: 'call',
      target: extractActionTarget(expression),
    };
  }

  if (command === 'window') {
    return {
      type: 'window',
      target: extractActionTarget(expression),
    };
  }

  if (command === 'exit') {
    return {
      type: 'exit',
    };
  }

  return {
    type: command,
    expression,
  };
}

function nodeToAction(node) {
  if (node.type === 'unknown' && /^THEN\b/i.test(getNodeRaw(node).trim())) {
    return parseActionText(getNodeRaw(node).trim().replace(/^THEN\b/i, '').trim());
  }

  if (node.type === 'goto') {
    return {
      type: 'goto',
      target: extractActionTarget(node.expression),
    };
  }

  if (node.type === 'call') {
    return {
      type: 'call',
      target: extractActionTarget(node.expression),
    };
  }

  if (node.type === 'window') {
    return {
      type: 'window',
      target: node.id,
      title: node.title,
    };
  }

  if (node.type === 'exit') {
    return {
      type: 'exit',
    };
  }

  if (['write', 'read'].includes(node.type)) {
    return {
      type: node.type,
      expression: node.expression,
    };
  }

  return parseActionText(getNodeRaw(node));
}

function findInlineAction(expression) {
  const thenMatch = expression.match(THEN_PATTERN);

  if (!thenMatch || !thenMatch[1].trim()) {
    return null;
  }

  return parseActionText(thenMatch[1]);
}

function findBlockAction(astNodes, ifIndex) {
  return findBranchAction(astNodes, ifIndex + 1, ['else', 'endif'], getThenAction, true);
}

export function analyzeActions(ast, ir) {
  const nextIr = cloneIr(ir);
  const menuActionLines = new Set(
    (nextIr.menus || []).flatMap((menu) => {
      return (menu.items || []).flatMap((item) => item.actionLines || [item.line]).filter(Boolean);
    })
  );

  nextIr.actions = [];
  nextIr.logic = buildLogic(ast.nodes, menuActionLines);

  ast.nodes.forEach((node, index) => {
    if (node.type !== 'if') {
      return;
    }

    const trigger = parseButtonTrigger(node.expression || '');

    if (!trigger) {
      return;
    }

    const inlineAction = findInlineAction(node.expression || '');
    const blockAction = inlineAction ? null : findBlockAction(ast.nodes, index);
    const action = inlineAction || blockAction?.action;

    if (!action) {
      return;
    }

    nextIr.actions.push({
      trigger,
      action,
      source: {
        line: node.line,
        nodeType: node.type,
        raw: node.raw,
        actionLine: blockAction?.sourceNode?.line || node.line,
      },
    });
  });

  return nextIr;
}
